import { languageFullNames } from "@/extensions/glossary/glossary";
import { whitelistLanguage } from "@/lib/i18n/locales";

/**
 * geoloc — detect the visitor's country/language/currency (ported from
 * to-use). Geolocation via omappapi, country metadata via the self-hosted
 * restcountries clone. Always resolves (graceful US/English/USD fallback);
 * the detected language is gated to our offered locales.
 */

// restcountries.com went paid — the project self-hosts a v3.1-compatible clone.
const RESTCOUNTRIES_BASE = "https://amato.capital:8787/v3.1";

export interface GeolocResult {
  country: string;
  location: string;
  language: string;       // one of our offered locale codes
  languageName: string;
  currency: string;
  failed?: boolean;
  original: { language: string; location: string; currency: string };
}

type Dict = Record<string, string>;

function invertDict(dict: Dict): Dict {
  return Object.fromEntries(Object.entries(dict).map(([k, v]) => [String(v), k]));
}

async function detect(): Promise<Omit<GeolocResult, "original">> {
  const fallback = {
    country: "United States",
    location: "United States",
    language: "en",
    languageName: "English",
    currency: "USD",
  };

  let cll = { ...fallback };

  try {
    const geoRes = await fetch("https://api.omappapi.com/v3/geolocate/json");
    const geo = await geoRes.json();
    cll = { ...cll, ...geo, location: geo.country };

    const country: string = geo.country;
    const inversion = invertDict(languageFullNames); // name → code
    const res = await fetch(
      `${RESTCOUNTRIES_BASE}/name/${encodeURIComponent(country)}?fullText=true`,
    );
    const q = await res.json();
    if (Array.isArray(q) && q[0]) {
      const langs = q[0].languages ?? {};
      const primaryLangName = langs[Object.keys(langs)[0]];
      const currencies = Object.keys(q[0].currencies ?? {});
      const detectedCode = inversion[primaryLangName];
      const language = whitelistLanguage(detectedCode);
      cll = {
        ...cll,
        currency: currencies[0] ?? fallback.currency,
        language,
        languageName: languageFullNames[language] ?? fallback.languageName,
      };
    }
  } catch {
    return { ...cll, failed: true };
  }

  return cll;
}

export default async function Geoloc(): Promise<GeolocResult> {
  const data = await detect();
  return {
    ...data,
    original: {
      language: data.language,
      location: data.location,
      currency: data.currency,
    },
  };
}

export { languageFullNames };
